import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { storage } from '../utils/storage';
import { ensureValidHexColor } from '../utils/colors';
import type { IconName } from '../utils/icons';
import type { Folder as FolderType } from '../types';
import { useAuthContext } from './AuthContext';
import { useFeaturesContext } from './FeaturesContext';
import { createHybridTheme } from '../themes/hybrid';
import type { ThemeName } from '../themes/tokens';
import { getCachedTheme, setCachedTheme, applyThemeToDom } from '../utils/themeCache';
import { logger } from '../utils/logger';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { MESSAGE_TYPES } from '../constants/background';
import { getPlatform } from '../infrastructure/platform';

// Mock Supabase client interface
interface MockSupabaseClient {
  auth: {
    getSession: () => Promise<{ data: { session: any | null }; error: Error | null }>;
  };
  from: (table: string) => {
    select: (columns?: string) => any;
    insert: (values: any, options?: any) => any;
    update: (values: any, options?: any) => any;
    delete: (options?: any) => any;
  };
}

// Mock implementation of getSupabaseClient
const getSupabaseClient = (): MockSupabaseClient => {
  console.debug('MOCK: Using mock Supabase client in SettingsContext');
  logger.debug('MOCK: Using mock Supabase client in SettingsContext');

  return {
    auth: {
      getSession: async () => ({ data: { session: null }, error: null }),
    },
    from: (_table: string) => ({
      select: (_columns?: string) => ({
        eq: () => ({
          single: async () => ({ data: null, error: null }),
          maybeSingle: async () => ({ data: null, error: null }),
        }),
      }),
      insert: (_values: any, _options?: any) => ({
        select: () => ({
          single: async () => ({ data: null, error: null }),
        }),
      }),
      update: (_values: any, _options?: any) => ({
        eq: () => ({
          select: () => ({
            single: async () => ({ data: null, error: null }),
          }),
        }),
      }),
      delete: (_options?: any) => ({
        eq: () => ({
          single: async () => ({ data: null, error: null }),
        }),
      }),
    }),
  };
};

interface UserSettings {
  user_id: string;
  theme: string;
  premium_status: 'free' | 'paid';
  features: Record<string, any>;
  folders: any[];
  prompts: any[];
  updated_at?: string;
  last_online_sync?: string | null;
}

interface SettingsContextType {
  currentTheme: string;
  setCurrentTheme: (theme: string) => Promise<void>;
  folderColors: Record<string, string>;
  folderIcons: Record<string, string>;
  updateFolderColor: (folderName: string, color: string) => Promise<void>;
  updateFolderIcon: (folderName: string, icon: IconName) => Promise<void>;
  resetFolderCustomizations: () => Promise<void>;
  isLoading: boolean;
  getThemeDefaultColor: () => string;
  settings: UserSettings | null;
}

const SettingsContext = createContext<SettingsContextType | null>(null);
const supabase = getSupabaseClient();

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export function useSettingsContext() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettingsContext must be used within a SettingsProvider');
  }
  return context;
}

interface SettingsProviderProps {
  children: React.ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  // Initialise from the synchronous theme cache so the first render already
  // matches the theme the DOM was bootstrapped with (see themeInit.js),
  // avoiding a light -> real-theme flash of the app shell and gradients.
  const [currentTheme, setTheme] = useState<string>(() => getCachedTheme() || 'light');
  const [folderColors, setFolderColors] = useState<Record<string, string>>({});
  const [folderIcons, setFolderIcons] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const { currentUser } = useAuthContext();
  const { enabledFeatures: _enabledFeatures } = useFeaturesContext();

  // Load settings from local cache and then refresh from server
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);

        // First load from local cache
        const [cachedTheme, cachedSettings, cachedFolders] = await Promise.all([
          storage.get(STORAGE_KEYS.THEME),
          storage.get(STORAGE_KEYS.SETTINGS),
          storage.get(STORAGE_KEYS.FOLDERS),
        ]);

        // Apply cached settings immediately. The DOM is applied by the effect
        // below (keyed on currentTheme); here we just sync state and the
        // synchronous cache so the next open paints the right theme instantly.
        const theme = cachedTheme || 'light';
        setTheme(theme);
        setCachedTheme(theme);
        if (cachedSettings?.settings) {
          setSettings(cachedSettings.settings);
        }

        // Apply cached folder settings
        if (cachedFolders) {
          const colors: Record<string, string> = {};
          const icons: Record<string, IconName> = {};

          for (const folder of cachedFolders) {
            colors[folder.name] = ensureValidHexColor(folder.color);
            icons[folder.name] = (folder.icon || 'Folder') as IconName;
          }

          setFolderColors(colors);
          setFolderIcons(icons);
        }

        setIsLoading(false);

        // If user is signed in, refresh settings from server in background
        if (currentUser?.id) {
          try {
            const { data: serverSettings } = await supabase
              .from('user_settings')
              .select('*')
              .eq('user_id', currentUser.id)
              .single();

            if (serverSettings) {
              // Update local cache and state with server settings
              const settingsWrapper = {
                premiumExpiryAttempts: 0,
                premiumLastChecked: Date.now(),
                settings: serverSettings,
              };
              await storage.set(STORAGE_KEYS.SETTINGS, settingsWrapper);
              setSettings(serverSettings);

              logger.debug('Settings refreshed from server', {
                module: 'settings',
                function: 'loadSettings',
                userId: currentUser.id,
              });
            }
          } catch (error) {
            // Log error but don't disrupt the UI since we're using cached settings
            logger.error(
              'Failed to refresh settings from server',
              error instanceof Error ? error : new Error('Unknown error'),
              {
                module: 'settings',
                function: 'loadSettings',
                userId: currentUser.id,
              }
            );
          }
        }
      } catch (error) {
        logger.error(
          'Error loading settings',
          error instanceof Error ? error : new Error('Unknown error'),
          {
            module: 'settings',
            function: 'loadSettings',
          }
        );
        // Use defaults on error but keep any existing cached settings
        setTheme('light');
        if (!settings) {
          setSettings(null);
        }
        setFolderColors({});
        setFolderIcons({});
      } finally {
        setIsLoading(false);
      }
    };

    void loadSettings();
    // Re-load only when the signed-in user changes; `settings` is read in the error
    // path to keep the cached value, and depending on it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  // Listen for settings updates from background script
  useEffect(() => {
    const handleUpdates = async (message: any) => {
      if (message.type === MESSAGE_TYPES.THEME_UPDATE && message.theme !== currentTheme) {
        setTheme(message.theme);
      } else if (message.type === MESSAGE_TYPES.SETTINGS_UPDATE && message.settings) {
        logger.debug('Received settings update', {
          module: 'settings',
          function: 'handleUpdates',
          settings: message.settings,
        });

        // Update both local cache and state
        if (message.settings?.settings) {
          await storage.set(STORAGE_KEYS.SETTINGS, message.settings);
          setSettings(message.settings.settings);
        }
      } else if (message.type === MESSAGE_TYPES.PREMIUM_STATUS_UPDATE) {
        // Update premium status in settings
        setSettings(prev =>
          prev
            ? {
                ...prev,
                premium_status: message.isPremium ? 'paid' : 'free',
              }
            : prev
        );
      }
    };

    return getPlatform().bus.subscribe(handleUpdates);
  }, [currentTheme]);

  // Apply theme changes to the DOM and keep the synchronous cache in sync.
  // Uses the same helper as the pre-mount bootstrap (themeInit.js) so the
  // markup applied on first paint and on later changes is identical. It
  // replaces the #theme-vars style in place (no cleanup) to avoid a transient
  // frame with no CSS variables whenever the theme changes.
  useEffect(() => {
    setCachedTheme(currentTheme);
    applyThemeToDom(currentTheme);
  }, [currentTheme]);

  const setCurrentTheme = async (theme: string) => {
    setTheme(theme);
    setCachedTheme(theme);
    await storage.set(STORAGE_KEYS.THEME, theme);
  };

  const getThemeDefaultColor = useCallback(() => {
    const theme = currentTheme as ThemeName;
    const hybridTheme = createHybridTheme(theme);
    return hybridTheme.colors.sidebar.folderIcon;
  }, [currentTheme]);

  const updateFolderColor = async (folderName: string, color: string) => {
    try {
      const validColor = ensureValidHexColor(color, getThemeDefaultColor());

      // Update state
      setFolderColors(prev => ({
        ...prev,
        [folderName]: validColor,
      }));

      // Update storage
      const folders: FolderType[] = (await storage.get(STORAGE_KEYS.FOLDERS)) || [];
      const updatedFolders = folders.map(folder =>
        folder.name === folderName ? { ...folder, color: validColor } : folder
      );
      await storage.set(STORAGE_KEYS.FOLDERS, updatedFolders);
    } catch (error) {
      console.error('Error updating folder color:', error);
    }
  };

  const updateFolderIcon = async (folderName: string, icon: IconName) => {
    // Update state
    setFolderIcons(prev => ({
      ...prev,
      [folderName]: icon,
    }));

    // Update storage
    const folders: FolderType[] = (await storage.get(STORAGE_KEYS.FOLDERS)) || [];
    const updatedFolders = folders.map(folder =>
      folder.name === folderName ? { ...folder, icon } : folder
    );
    await storage.set(STORAGE_KEYS.FOLDERS, updatedFolders);
  };

  // Note: we intentionally do NOT gate the whole tree behind a full-screen
  // spinner while settings load. The theme is already applied synchronously
  // before mount, and each surface handles its own data-loading state, so
  // rendering children immediately avoids a spinner -> content remount flash
  // on every open. `isLoading` remains available via context for consumers
  // that want it.

  const resetFolderCustomizations = async () => {
    try {
      // Get the default color for the current theme
      const defaultColor = getThemeDefaultColor();

      // Get folders from storage
      const folders: FolderType[] = (await storage.get(STORAGE_KEYS.FOLDERS)) || [];

      // Reset all folder icons to "Folder" and colors to the theme default
      const updatedFolders = folders.map(folder => ({
        ...folder,
        icon: 'Folder',
        color: defaultColor,
      }));

      // Update storage
      await storage.set(STORAGE_KEYS.FOLDERS, updatedFolders);

      // Update state
      const colors: Record<string, string> = {};
      const icons: Record<string, IconName> = {};

      for (const folder of updatedFolders) {
        colors[folder.name] = defaultColor;
        icons[folder.name] = 'Folder' as IconName;
      }

      setFolderColors(colors);
      setFolderIcons(icons);

      logger.debug('Reset folder customizations', {
        module: 'settings',
        function: 'resetFolderCustomizations',
        foldersCount: updatedFolders.length,
      });
    } catch (error) {
      logger.error(
        'Error resetting folder customizations',
        error instanceof Error ? error : new Error('Unknown error'),
        {
          module: 'settings',
          function: 'resetFolderCustomizations',
        }
      );
    }
  };

  return (
    <SettingsContext.Provider
      value={{
        currentTheme,
        setCurrentTheme,
        folderColors,
        folderIcons,
        updateFolderColor,
        updateFolderIcon,
        resetFolderCustomizations,
        isLoading,
        getThemeDefaultColor,
        settings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}
